import { useCallback, useState } from "react";

/**
 * Reads and writes the member-portal bearer `session` token.
 *
 * The token lives in `localStorage`, so the session survives a tab close and is
 * shared with new tabs in the same browser. The security tradeoff: this is a
 * PHI session, so on a shared or public machine the token persists until
 * explicit sign-out or server-side expiry instead of clearing when the tab
 * closes. The member SPA has no OIDC. After login, this self-issued bearer,
 * scoped to one patient, is the only auth on `/api/portal/*` calls, passed
 * through `apiFetch`'s `token` option.
 *
 * On a portal `ApiError` with `status === 401`, callers `clear()` the session
 * and the UI falls back to the login view.
 */
const SESSION_KEY = "db.portal.session";

export interface PortalSession {
  session: string | null;
  setSession: (token: string) => void;
  clear: () => void;
}

export function usePortalSession(): PortalSession {
  const [session, setSessionState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(SESSION_KEY);
    } catch {
      // localStorage can throw in privacy modes / sandboxed iframes.
      return null;
    }
  });

  const setSession = useCallback((token: string): void => {
    try {
      localStorage.setItem(SESSION_KEY, token);
    } catch {
      // Ignore a persistence failure and keep the token in memory for this tab.
    }
    setSessionState(token);
  }, []);

  const clear = useCallback((): void => {
    try {
      localStorage.removeItem(SESSION_KEY);
    } catch {
      // Ignore.
    }
    setSessionState(null);
  }, []);

  return { session, setSession, clear };
}
